/**
 * Capital Gains Tax engine — the single place that decides the TAXABLE capital gain and the
 * 30% minimum-tax floor, under either the legacy 50% discount regime or the 1-Jul-2027 reform.
 * Ported verbatim from frontend/src/calculations/capitalGainsTax.js — see the golden-master
 * regression suite (server/tests/rest/calculation/goldenMaster/) for the parity proof.
 *
 * NOTE (main residence): the family home stays CGT-free under the reform — this engine is for
 * ASSESSABLE assets (investment property, shares/managed funds, units), never the main residence.
 */
import { CGT_REGIME } from './constants/cgtReform.ts';
import type { FactFindAsset } from './factFindTypes.ts';

const MS_PER_YEAR = 365.25 * 24 * 3600 * 1000;

export interface CgtRegimeConfig {
    reformEnabled: boolean;
    commencementDate: string;
    discountRate: number;
    minDiscountHoldingMonths: number;
    minimumTaxRate: number;
    incomeSupportExempt: boolean;
    affordableHousingDiscountRate: number;
    assumedCostBaseFraction: number;
}

export type DisposalPhase = 'old' | 'new' | 'straddle';

/** Whole/fractional years between two ISO dates (or Date objects). */
export function yearsBetween(from: Date | string, to: Date | string): number {
    const a = new Date(from).getTime();
    const b = new Date(to).getTime();
    if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
    return Math.max(0, (b - a) / MS_PER_YEAR);
}

/** Indexed cost base = cost base grown by CPI over the holding period (compound). */
export function indexedCostBase(costBase: number, years: number, cpiRate: number): number {
    const cb = Number(costBase) || 0;
    if (!(cb > 0) || !(years > 0) || !(cpiRate > 0)) return cb;
    return cb * Math.pow(1 + cpiRate, years);
}

/**
 * Estimate an asset's value at the reform commencement date using the ATO-style apportionment
 * formula: value = costBase * (proceeds/costBase) ^ (preYears/totalYears).
 */
export function valueAtCommencement({
    costBase,
    proceeds,
    holdingYears,
    preYears,
}: {
    costBase: number;
    proceeds: number;
    holdingYears: number;
    preYears: number;
}): number {
    const cb = Number(costBase) || 0;
    const p = Number(proceeds) || 0;
    if (!(cb > 0) || !(holdingYears > 0) || !(preYears > 0) || p <= 0) return cb;
    const fraction = Math.min(1, preYears / holdingYears);
    return cb * Math.pow(p / cb, fraction);
}

/** Classify a disposal relative to the reform commencement date. */
export function classifyDisposal(
    acquisitionDate: Date | string,
    disposalDate: Date | string,
    config: CgtRegimeConfig = CGT_REGIME,
): { phase: DisposalPhase; holdingYears: number; preYears: number; postYears: number } {
    const holdingYears = yearsBetween(acquisitionDate, disposalDate);
    if (!config.reformEnabled) return { phase: 'old', holdingYears, preYears: holdingYears, postYears: 0 };
    const commence = new Date(config.commencementDate).getTime();
    const acq = new Date(acquisitionDate).getTime();
    const disp = new Date(disposalDate).getTime();
    if (disp <= commence) return { phase: 'old', holdingYears, preYears: holdingYears, postYears: 0 };
    if (acq >= commence) return { phase: 'new', holdingYears, preYears: 0, postYears: holdingYears };
    return {
        phase: 'straddle',
        holdingYears,
        preYears: yearsBetween(acquisitionDate, config.commencementDate),
        postYears: yearsBetween(config.commencementDate, disposalDate),
    };
}

/**
 * The TAXABLE capital gain, broken into the pre-commencement (50% discount) and post-commencement
 * (indexed) portions. Accepts EITHER pre-computed phase/years (pure, for tests) OR raw dates.
 */
export function computeCapitalGain(params: any): {
    grossGain: number;
    preCommencementTaxable: number;
    postCommencementTaxable: number;
    totalTaxable: number;
    valueAtCommencement: number;
    phase: DisposalPhase;
} {
    const config: CgtRegimeConfig = params.config || CGT_REGIME;
    const cb = Math.max(0, Number(params.costBase) || 0);
    const proceeds = Math.max(0, Number(params.proceeds) || 0);
    const cpiRate = params.cpiRate != null ? Number(params.cpiRate) : 0;
    const discountRate = params.discountRate != null ? Number(params.discountRate) : config.discountRate;

    const { phase, holdingYears, preYears, postYears } = params.phase
        ? params
        : classifyDisposal(params.acquisitionDate, params.disposalDate, config);

    const grossGain = Math.max(0, proceeds - cb);
    const eligibleForDiscount = holdingYears >= config.minDiscountHoldingMonths / 12;
    const zero = { grossGain: 0, preCommencementTaxable: 0, postCommencementTaxable: 0, totalTaxable: 0, valueAtCommencement: cb, phase };
    if (grossGain <= 0) return zero;

    if (phase === 'old') {
        const taxable = eligibleForDiscount ? grossGain * (1 - discountRate) : grossGain;
        return { grossGain, preCommencementTaxable: taxable, postCommencementTaxable: 0, totalTaxable: taxable, valueAtCommencement: cb, phase };
    }

    if (phase === 'new') {
        const idxCb = indexedCostBase(cb, holdingYears, cpiRate);
        const taxable = Math.max(0, proceeds - idxCb);
        return { grossGain, preCommencementTaxable: 0, postCommencementTaxable: taxable, totalTaxable: taxable, valueAtCommencement: cb, phase };
    }

    // straddle: 50% discount on the gain to commencement, indexation on the gain after.
    const valComm =
        params.valueAtCommencement != null
            ? Number(params.valueAtCommencement)
            : valueAtCommencement({ costBase: cb, proceeds, holdingYears, preYears });
    const preGross = Math.max(0, valComm - cb);
    const preTaxable = eligibleForDiscount ? preGross * (1 - discountRate) : preGross;
    const idxValComm = indexedCostBase(valComm, postYears, cpiRate);
    const postTaxable = Math.max(0, proceeds - idxValComm);
    return {
        grossGain,
        preCommencementTaxable: preTaxable,
        postCommencementTaxable: postTaxable,
        totalTaxable: preTaxable + postTaxable,
        valueAtCommencement: valComm,
        phase,
    };
}

/**
 * Legacy 50% discount applied to a PRE-COMPUTED gross capital gain (callers assume a 12-month+ hold).
 * The date-aware reform path uses {@link computeCapitalGain}.
 */
export function legacyAssessableGain(grossGain: number, config: CgtRegimeConfig = CGT_REGIME): number {
    const g = Math.max(0, Number(grossGain) || 0);
    return g * (1 - config.discountRate);
}

/** Assets with no captured acquisition date default to "long held / pre-2027" (keeps the discount). */
export const DEFAULT_ACQUISITION_DATE = '2000-01-01';

/**
 * The asset's real acquisition date, from the fact-find's `acquiredOn` (`YYYY-MM`), or null when it was
 * not recorded. Returns the FIRST of the month — within-month precision is immaterial to both the
 * 12-month discount test and the 1-Jul-2027 apportionment, and month granularity is all the fact-find
 * asks the client for.
 *
 * Built with explicit Y/M parts rather than `new Date('YYYY-MM')`, which parses as UTC midnight and can
 * land in the previous month for users behind UTC.
 */
export function resolveAcquisitionDate(asset: Pick<FactFindAsset, 'acquiredOn'> | null | undefined): Date | null {
    const raw = typeof asset?.acquiredOn === 'string' ? asset.acquiredOn.trim() : '';
    const match = /^(\d{4})-(\d{2})$/.exec(raw);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;
    // Reject implausible years rather than silently producing a wild holding period.
    if (year < 1900 || year > 2200) return null;
    return new Date(year, month - 1, 1);
}

/**
 * Resolve an asset's cost base for CGT: the entered figure if present, otherwise the assumed default
 * (25% of current value — see CGT_REGIME.assumedCostBaseFraction).
 */
export function resolveCostBase(
    asset: Pick<FactFindAsset, 'costBase' | 'value'> | null | undefined,
    config: CgtRegimeConfig = CGT_REGIME,
): { costBase: number; assumed: boolean } {
    const entered = asset?.costBase ?? 0;
    if (entered > 0) return { costBase: entered, assumed: false };
    const value = asset?.value ?? 0;
    return { costBase: value * config.assumedCostBaseFraction, assumed: value > 0 };
}

/** CGT started on 20 September 1985; assets acquired before then are generally exempt. */
const CGT_START_YEAR = 1985;
const CGT_START_MONTH = 9; // September — the 20th, hence the boundary-month note below

/**
 * Is this a pre-CGT (pre-20-Sept-1985) asset, i.e. CGT-exempt?
 *
 * Derived from the recorded acquisition month (`acquiredOn`) where there is one — the fact-find asks for
 * the date and nothing else, so the exemption follows from it rather than being separately asserted.
 * The pre/post `acquisitionDate` flag it replaced is still honoured as a fallback for records saved
 * before the date field existed.
 *
 * September 1985 is deliberately treated as NOT exempt: the statutory boundary is the 20th, which month
 * granularity cannot resolve, so the assessable (higher-tax) side is chosen rather than granting an
 * exemption that might not exist.
 *
 * A blank/unset costBase is "unknown" (=> the 25% assumed default, see resolveCostBase), never exempt.
 */
export function isPreCgtAsset(asset: Pick<FactFindAsset, 'acquisitionDate' | 'acquiredOn'> | null | undefined): boolean {
    const acquired = resolveAcquisitionDate(asset);
    if (acquired) {
        const year = acquired.getFullYear();
        if (year !== CGT_START_YEAR) return year < CGT_START_YEAR;
        return acquired.getMonth() + 1 < CGT_START_MONTH;
    }
    return asset?.acquisitionDate === 'pre';
}

/**
 * Date-aware TAXABLE gain from a sale — the wiring the call sites use.
 */
export function assessableGainForSale({
    costBase,
    proceeds,
    acquisitionDate,
    disposalDate = new Date(),
    cpiRate = 0,
    config = CGT_REGIME,
}: {
    costBase: number;
    proceeds: number;
    acquisitionDate?: string | Date;
    disposalDate?: Date | string;
    cpiRate?: number;
    config?: CgtRegimeConfig;
}): number {
    return computeCapitalGain({
        costBase,
        proceeds,
        acquisitionDate: acquisitionDate || DEFAULT_ACQUISITION_DATE,
        disposalDate,
        cpiRate,
        config,
    }).totalTaxable;
}

export function applyMinimumTax({
    postCommencementTaxable,
    marginalTaxOnPost,
    isIncomeSupportRecipient,
    config = CGT_REGIME,
}: {
    postCommencementTaxable: number;
    marginalTaxOnPost: number;
    isIncomeSupportRecipient: boolean;
    config?: CgtRegimeConfig;
}): number {
    const g = Math.max(0, Number(postCommencementTaxable) || 0);
    const marginal = Math.max(0, Number(marginalTaxOnPost) || 0);
    if (!config.reformEnabled || g <= 0) return marginal;
    if (isIncomeSupportRecipient && config.incomeSupportExempt) return marginal;
    return Math.max(marginal, g * config.minimumTaxRate);
}
